'use client';

import { usePathname } from 'next/navigation';
import { Menu, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn, isRouteActive } from '@kit/ui/utils';

import { BrandLink } from '~/components/brand-link';
import { resolveShellNav } from '~/config/navigation/resolve-shell-nav';
import type { Brand } from '~/lib/brand';
import type { UserWorkspace } from '../_lib/server/load-user-workspace';
import { HomeMobileNavigation } from './home-mobile-navigation';

const tabClass = (active: boolean) =>
  cn(
    'flex h-full w-full min-w-0 flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium transition-colors',
    active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
  );

function TabIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon className="size-5" aria-hidden />;
}

// Tailwind needs literal class names; one entry per possible column count
// (1 to MAX_PRIMARY_TABS tabs, plus More).
const GRID_COLUMNS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

/**
 * Fixed bottom tab bar — the mobile navigation surface. The tabs are DERIVED
 * from the acting role's own menu (`resolveShellNav().primaryTabs`): each tab
 * is a ranked leaf of the same sections the desktop sidebar and the More
 * sheet render, so a client gets their portal, an admin gets Cast Sheet, and a
 * dancer gets their pipeline — and no tab can point outside the role's menu.
 * "More" opens the full menu sheet (HomeMobileNavigation with a custom
 * trigger). Desktop (md+) hides the bar.
 *
 * Mount it as a sibling of the page content together with a
 * `h-(--app-bottom-inset)` spacer so the fixed bar never covers the last
 * rows of content. Its `data-app-bottom-chrome` attribute sets that variable
 * (styles/globals.css), which fixed footers also use to sit above the bar.
 * The row height comes from `--app-tab-bar-height`, the same variable the
 * inset is computed from, so the two cannot drift.
 */
export function MobileTabBar(props: {
  workspace: UserWorkspace;
  brand?: Brand;
  /** Explicit `lang` cookie choice from the server layout; `null` = Automatic. */
  selectedLocale: string | null;
}) {
  const pathname = usePathname();
  const { primaryTabs } = resolveShellNav(
    props.workspace,
    props.brand ?? 'flow',
  );
  // One active tab: the most specific tab whose path prefixes the URL, so
  // Home (`/home`) does not light up beside Inbox (`/home/inbox`), nor the
  // client Home beside Cast Sheet.
  const matchedPath = primaryTabs
    .filter((tab) => isRouteActive(tab.path, pathname, tab.end ?? false))
    .reduce<
      string | null
    >((best, tab) => (best === null || tab.path.length > best.length ? tab.path : best), null);
  // `/home` is every role's landing page, but a role's Home tab can point
  // elsewhere (a client's is `/home/client-user`). The rank-1 tab is the
  // role's Home (nav-sections.ts), so it is the current page on `/home`
  // (mobile cast audit F10).
  const activePath =
    matchedPath ??
    (pathname === '/home' ? (primaryTabs[0]?.path ?? null) : null);
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('common:primaryNavigation')}
      className={cn(
        'border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-(--z-app-chrome) border-t backdrop-blur md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
      )}
      data-testid="mobile-tab-bar"
      // Sets --app-bottom-inset to the bar height (styles/globals.css).
      data-app-bottom-chrome=""
    >
      <div
        className={cn(
          'grid h-(--app-tab-bar-height)',
          GRID_COLUMNS[primaryTabs.length + 1] ?? 'grid-cols-5',
        )}
      >
        {primaryTabs.map((tab) => (
          // BrandLink: an admin tab on the Ballee host is a cross-brand hop
          // and goes through the authenticated handoff.
          <BrandLink
            key={tab.path}
            to={tab.path}
            className={tabClass(tab.path === activePath)}
            aria-current={tab.path === activePath ? 'page' : undefined}
          >
            <TabIcon icon={tab.icon} />
            <span className="max-w-full truncate">
              <Trans i18nKey={tab.label} defaults={tab.label} />
            </span>
          </BrandLink>
        ))}

        <HomeMobileNavigation
          workspace={props.workspace}
          brand={props.brand}
          selectedLocale={props.selectedLocale}
          trigger={
            <Button variant="ghost" type="button" className={tabClass(false)}>
              <TabIcon icon={Menu} />
              <Trans i18nKey="common:more" defaults="More" />
            </Button>
          }
        />
      </div>
    </nav>
  );
}
